import type { ReactElement } from "react";
import NextLink from "next/link";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  ButtonGroup,
  Center,
  Heading,
  Image,
  SkeletonText,
  Stack,
  Text,
} from "@chakra-ui/react";
import useSWR from "swr";
import TextLink from "components/common/link/text-link";
import useUser from "hooks/use-user";
import type { MasterAccessResponse } from "types/api/auth/master-access";

const LimitWarning = (): ReactElement => {
  const user = useUser();
  const isDirectAccess = user.details?.directAccess ?? false;
  const isBasic = user?.access?.group === "free";
  const searchType = (window.location && window.location.pathname) ? window.location.pathname.split('/').slice(0)[1] : null;

  const { data: accessChecks } = useSWR<MasterAccessResponse>(
    "/system/auth/master-access"
  );
  const allowSubsidiaryUsers = accessChecks?.allowSubsidiaryUsers ?? false;

  const isLoaded = !user.loading && !!accessChecks;

  if (isBasic) {
    return (
      <Center h="100%">
        <Stack
          direction={["column-reverse", "column-reverse", "row"]}
          align={["center", "center", "flex-end"]}
          spacing={12}
          rounded="xl"
          shadow="lg"
          bg="white"
          padding={[4, 6, 8, 10]}
          m={4}
          w="100%"
          maxW="1200px"
        >
          <Image
            src="/assets/upgrade-banner.svg"
            alt="Limit"
            maxW="350px"
            w="100%"
            h="auto"
          />

          <Center textAlign="center" flexDirection="column" color="blue.700">
            <Heading mb={6}>You&apos;ve Reached Your Monthly Limit</Heading>

            <Text
              fontSize={["lg", "xl"]}
              fontWeight={500}
              mb={6}
              lineHeight={1.66}
            >
              You&apos;ve reached the monthly limit to your free PrivCo basic
              account. Upgrade today for the complete solution to your Private
              company research needs.
            </Text>

            <Button
              as={NextLink}
              href="/account"
              size="lg"
              colorScheme="purple"
            >
              Upgrade Now
            </Button>
          </Center>
        </Stack>
      </Center>
    );
  }

  const getLimitMessage = () => {
    if (isDirectAccess) {
      if (allowSubsidiaryUsers) {
        return (
          <>
            <Text>
              Your institution has reached its Direct Access profile view limit.
              For continued access, please sign in with your individual PrivCo
              account if you have created one already, or create one here. If
              you have any questions please contact your library or{" "}
              <TextLink href="mailto:support@privco.com" isExternal>
                support@privco.com
              </TextLink>
              .
            </Text>

            <Stack
              as={ButtonGroup}
              colorScheme="blue"
              size="sm"
              mt={2}
              direction={["column", "row"]}
              justifyContent="flex-start"
              spacing={2}
            >
              <Button onClick={() => user.signOut()}>
                Sign In with Academic Account
              </Button>
              <Button onClick={() => user.signOut("/academic-signup")}>
                Create Academic Account
              </Button>
            </Stack>
          </>
        );
      }

      return (
        <Text>
          Your institution has reached its profile view limit. If you have any
          questions please contact your library, or{" "}
          <TextLink href="mailto:support@privco.com" isExternal>
            support@privco.com
          </TextLink>{" "}
          and your PrivCo account manager will get back to you as soon as
          possible.
        </Text>
      );
    }

    if (searchType && searchType === "company-contact") {
      return (
        <Text>
          It seems like you have reached your contact view limit! If
          you believe this is an error, please contact{" "}
          <TextLink href="mailto:support@privco.com" isExternal>
            support@privco.com
          </TextLink>{" "}
          and your PrivCo account manager will get back to you as soon as
          possible.
        </Text>
      );
    }

    return (
      <Text>
        It seems like you have reached your monthly limit of profile views! If
        you believe this is an error, please contact{" "}
        <TextLink href="mailto:support@privco.com" isExternal>
          support@privco.com
        </TextLink>{" "}
        and your PrivCo account manager will get back to you as soon as
        possible.
      </Text>
    );
  };

  return (
    <Center h="100%">
      <Alert
        status="warning"
        rounded="md"
        maxW="510px"
        shadow="lg"
        overflow="hidden"
      >
        <AlertIcon />
        <Box>
          <AlertTitle>Limit Reached!</AlertTitle>

          <AlertDescription>
            <SkeletonText noOfLines={4} mt={1} spacing={4} isLoaded={isLoaded}>
              {getLimitMessage()}
            </SkeletonText>
          </AlertDescription>
        </Box>
      </Alert>
    </Center>
  );
};

export default LimitWarning;
